import { getDistinctValues, ICrop, ICropping, IOmniaShell, isArrayEmpty, models, selectMany } from "../../omnia";

export function confirmCropChange(shell: IOmniaShell, crop: ICrop, targets: ICropping[]): ng.IPromise<boolean> {

    const cropId = (crop ? crop.CropId : 0);

    const changed = targets.filter(t => t.CropId && (t.CropId != cropId));

    if (!changed.length) return shell.resolve(true);

    const fields = targets.map(t => "- " + t.Field.Name + " (" + t.Area + " ha)").join("<br>");

    const msg = `This will change the details for the crops in the following fields:<br><br>${fields}<br><br>Do you want to continue?`;

    return omnia.shell.showOkCancel(shell, msg);

}

export function checkChangeImpactsOnPlans(shell: IOmniaShell, model: ICropping, target: ICropping) {

    const areaChanged = model
        && target
        && (target.Area != model.Area)
        && (target.Plans && (target.Plans.length > 0))

    return areaChanged
        ? confirmChangeImpactsOnPlans(shell)
        : true

}

export function confirmChangeImpactsOnPlans(shell: IOmniaShell) {

    const msg = `<p>This crop is already part of a plan and changing the area of this crop will change the product totals required within the plan.</p>
            <p>You need to recalculate the plans for the new area to be used.</p>`

    const title = "Recalculate plans"

    return omnia.shell.showOkCancel(shell, msg, title)

}

export function confirmChangeOnNutritionPlans(shell: IOmniaShell, target: ICropping) {

    const maxYear = target.YearId + 1

    const getNext = (c: ICropping): ICropping[] => {
        const next = (c.NextCroppings || []).filter(n => n.YearId <= maxYear)
        return [c].concat(selectMany(next.map(getNext)))
    }

    const all = getNext(target).filter(c => c.InNutritionPlan)

    if (isArrayEmpty(all)) {
        return true
    }

    const plans = getDistinctValues(
        selectMany(all.map(c => (c.Plans || []).filter(p => p.Type == models.PlanType.NutritionPlan || p.Type == models.PlanType.NutritionPlan2024))),
        p => p,
        p => p.PlanId
    )

    if (isArrayEmpty(plans)) {
        return true
    }

    const planNames = plans.map(p => `<li>- ${p.Name} (${p.Year})</li>`).join("")

    const title = "Recalculate nutrition plans";

    const msg = `<p>This crop and/or some of its successors are already part of a nutrition plan, and the changes may impact those plans.</p>
            <p>You may need to recalculate the following plans to account for the changes:</p>
            <ul class="l" style="margin: 10px;">${planNames}</ul>`

    return omnia.shell.showOkCancel(shell, msg, title)

}

export function confirmChangeImpactsOnRequirements(shell: IOmniaShell, model: ICropping, target: ICropping, targets: ICropping[]): ng.IPromise<any> {

    const a = model

    if (target && (!target.HasNutritionRequirements)) {
        return shell.resolve(true)
    }

    const changes = targets.filter(b =>
        (a.CropId != b.CropId)
        || (a.VarietyId != b.VarietyId)
        || (a.TargetYield != b.TargetYield)
        || (a.Residue != b.Residue)
        || (a.GrassManagement != b.GrassManagement)
        || (a.GrassCuts != b.GrassCuts)
        || (a.BiomassLevel != b.BiomassLevel)
        || (a.StrawMulch != b.StrawMulch)
        || (a.GrowingSeasonLength != b.GrowingSeasonLength)
        || (a.NLeafAnalysis != b.NLeafAnalysis)
        || (a.MaltingOption != b.MaltingOption)
        || (a.HighNDistilling != b.HighNDistilling)
        || (a.Undersown != b.Undersown)
        || (a.GrassSiteClass != b.GrassSiteClass)
        || (a.NCropGroup != b.NCropGroup)
        || (a.TargetpH != b.TargetpH)
        || (a.ManureSpreadArea != b.ManureSpreadArea)
    )

    if (changes.length) {

        const title = "Recalculate crop requirements";

        const msg = `<p>You have changed parameters that affect the crop requirements.</p>
            <p>You need to recalculate the crop requirements for the these changes to be taken into account. Please note that the plans will also need to be recalculated for the new requirements to be taken into account.`

        return omnia.shell.showOkCancel(shell, msg, title);

    }

    return shell.resolve(true);

}

export function getCroppingName(cropping: ICropping): string {
    return cropping.Field.Name + " - " + cropping.CropName + " (" + cropping.Area + " ha)";
}
